import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/context/AuthContext";
import { FileBarChart, Plus, X, Loader2, Printer, Trash2, Eye, Brain, Download } from "lucide-react";

const REPORT_TYPES = [
  { value: "executive", label: "Ejecutivo", desc: "Resumen para comité de dirección" },
  { value: "technical", label: "Técnico", desc: "Detalle técnico de proyectos" },
  { value: "budget", label: "Presupuestario", desc: "Análisis financiero detallado" },
  { value: "resources", label: "Recursos", desc: "Capacidad y asignación de recursos" },
];

export default function Reports() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ title: "", type: "executive", project_ids: [] });
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(`${API}/reports`),
        axios.get(`${API}/projects`),
      ]);
      setReports(rRes.data);
      setProjects(pRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/reports/generate`, form);
      setShowModal(false);
      setReports(prev => [data, ...prev]);
      if (data.status === "ready") setViewing(data);
      else {
        // Poll for completion
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          if (attempts > 20) { clearInterval(poll); return; }
          const res = await axios.get(`${API}/reports/${data.id}`);
          if (res.data.status === "ready") {
            clearInterval(poll);
            setViewing(res.data);
            setReports(prev => prev.map(r => r.id === data.id ? res.data : r));
          } else if (res.data.status === "error") {
            clearInterval(poll);
          }
        }, 3000);
      }
    } catch (e) { alert("Error al generar informe"); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar informe?")) return;
    await axios.delete(`${API}/reports/${id}`);
    setReports(prev => prev.filter(r => r.id !== id));
    if (viewing?.id === id) setViewing(null);
  };

  const handleView = async (report) => {
    if (report.html_content) { setViewing(report); return; }
    try {
      const { data } = await axios.get(`${API}/reports/${report.id}`);
      setViewing(data);
    } catch (e) { alert("Error"); }
  };

  const handlePrint = () => {
    if (!viewing?.html_content) return;
    const blob = new Blob([viewing.html_content], { type: "text/html; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (w) w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 5000); }
    else URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if (!viewing?.id) return;
    try {
      const response = await axios.get(`${API}/reports/${viewing.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe_${viewing.id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Error al generar el PDF"); }
  };

  const toggleProject = (pid) => {
    setForm(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(pid) ? prev.project_ids.filter(p => p !== pid) : [...prev.project_ids, pid]
    }));
  };

  const typeMap = { executive: "Ejecutivo", technical: "Técnico", budget: "Presupuestario", resources: "Recursos" };
  const statusColor = { ready: "bg-green-100 text-green-700", generating: "bg-yellow-100 text-yellow-700", error: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">{t("reports.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("reports.subtitle")}</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] flex items-center gap-2 shadow-sm" data-testid="generate-report-btn">
          <Plus size={16} /> {t("reports.generate")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Reports list */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">Informes generados ({reports.length})</h2>
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl loading-shimmer" />)
          ) : reports.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <Brain size={32} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Sin informes generados</p>
              <p className="text-xs text-gray-400 mt-1">Genera tu primer informe ejecutivo con Pando</p>
            </div>
          ) : (
            reports.map(r => (
              <div key={r.id}
                className={`bg-white rounded-xl border cursor-pointer transition-all p-4 ${viewing?.id === r.id ? "border-[#007934] shadow-md" : "border-gray-100 hover:border-gray-200"}`}
                onClick={() => handleView(r)} data-testid={`report-item-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{typeMap[r.type] || r.type}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusColor[r.status] || "bg-gray-100 text-gray-600"}`}>{r.status}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{r.generated_by}</p>
                    <p className="text-xs text-gray-300">{new Date(r.created_at).toLocaleString("es-ES")}</p>
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleDelete(r.id); }}
                    className="text-gray-200 hover:text-red-500 transition-colors ml-2" data-testid={`delete-report-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Report viewer */}
        <div className="lg:col-span-2">
          {viewing ? (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{viewing.title}</h3>
                  <p className="text-xs text-gray-400">{typeMap[viewing.type]} · {new Date(viewing.created_at).toLocaleString("es-ES")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handlePrint} className="flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50" data-testid="print-report-btn">
                    <Printer size={14} /> Imprimir
                  </button>
                  <button onClick={handleDownloadPdf} className="flex items-center gap-2 bg-[#007934] text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#006528]" data-testid="download-report-pdf-btn">
                    <Download size={14} /> Descargar PDF
                  </button>
                  <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
              </div>
              {viewing.status === "generating" ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <Brain size={24} className="text-[#007934] animate-pulse" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-900">Generando informe con Pando...</p>
                    <p className="text-sm text-gray-500 mt-1">Perplexity AI está analizando los datos</p>
                  </div>
                  <Loader2 size={24} className="animate-spin text-[#007934]" />
                </div>
              ) : viewing.status === "error" ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <p className="text-red-500 font-medium">Error al generar el informe</p>
                  <p className="text-sm text-gray-400 mt-1">Verifica la API key de Perplexity</p>
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  srcDoc={viewing.html_content}
                  className="w-full border-0"
                  style={{ height: "70vh" }}
                  title="Informe ejecutivo"
                  data-testid="report-iframe"
                />
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 flex flex-col items-center justify-center h-96">
              <FileBarChart size={48} className="text-gray-200 mb-4" />
              <p className="text-gray-500 font-medium">Selecciona un informe para verlo</p>
              <p className="text-sm text-gray-400 mt-1">O genera uno nuevo con el botón de arriba</p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-fade-up" data-testid="generate-report-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-lg font-bold font-['Outfit']">Generar Informe con Pando</h2>
                <p className="text-xs text-gray-400 mt-0.5">Powered by Perplexity AI</p>
              </div>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleGenerate} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Título del informe *</label>
                <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="Ej: Informe Ejecutivo Q1 2026"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="report-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo de informe</label>
                <div className="grid grid-cols-2 gap-2">
                  {REPORT_TYPES.map(t => (
                    <button type="button" key={t.value} onClick={() => setForm({...form, type: t.value})}
                      className={`p-3 rounded-lg border text-left transition-all ${form.type === t.value ? "border-[#007934] bg-[#E8F5E9]" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`report-type-${t.value}`}>
                      <p className={`text-sm font-medium ${form.type === t.value ? "text-[#007934]" : "text-gray-900"}`}>{t.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Proyectos a incluir <span className="text-gray-400 text-xs">(vacío = todos)</span></label>
                <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-2">
                  {projects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={form.project_ids.includes(p.id)} onChange={() => toggleProject(p.id)}
                        className="rounded border-gray-300 text-[#007934] focus:ring-[#007934]" />
                      <span className="text-sm text-gray-700">{p.title}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={generating} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="confirm-generate-btn">
                  {generating ? <><Loader2 size={14} className="animate-spin" /> Generando...</> : <><Brain size={14} /> Generar con Pando</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
